import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { StatusPill } from '@bb/ui';
import type { A11yCheck, AdminApi } from '../../../api/client.ts';

/** Waits until the editor stops typing before asking the server again. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * Accessibility issues of the message being edited (BB-60), refreshed as it changes: missing alt text, low contrast
 * (with the brand theme's colours), missing labels, small tap targets, missing accessibility language. Error-level
 * issues block publishing; the composer learns it through `onBlockingChange`.
 */
export function A11yIssuesPanel({
  environmentId,
  channel,
  content,
  themeId,
  onBlockingChange,
  debounceMs = 400,
}: {
  environmentId: string;
  channel: 'content_card' | 'in_app' | 'banner';
  content: Record<string, unknown>;
  themeId?: string;
  onBlockingChange?: (blocking: boolean) => void;
  debounceMs?: number;
}) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const settled = useDebounced(JSON.stringify(content), debounceMs);
  const check = useQuery<A11yCheck>({
    queryKey: ['a11y', environmentId, channel, themeId ?? null, settled],
    queryFn: () => api.checkA11y(environmentId, { channel, content: JSON.parse(settled) as Record<string, unknown>, ...(themeId ? { themeId } : {}) }),
    placeholderData: keepPreviousData,
  });
  const blocking = check.data?.blocking ?? false;
  useEffect(() => onBlockingChange?.(blocking), [blocking, onBlockingChange]);
  return (
    <section aria-label="Accessibility" className="panel">
      <h3>
        Accessibility{' '}
        {check.data && (check.data.issues.length === 0 ? <StatusPill tone="success">No issues</StatusPill> : blocking ? <StatusPill tone="danger">Blocks publishing</StatusPill> : <StatusPill tone="warning">{check.data.issues.length} to review</StatusPill>)}
      </h3>
      {check.isError && <p role="alert">The accessibility check could not run.</p>}
      {check.data && check.data.issues.length > 0 && (
        <ul aria-label="Accessibility issues">
          {check.data.issues.map((issue) => (
            <li key={`${issue.code}/${issue.path}`}>
              <StatusPill tone={issue.severity === 'error' ? 'danger' : 'warning'}>{issue.severity}</StatusPill> {issue.message} <code className="hint">{issue.path}</code>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
